import { useEffect } from "react";

// Keep formatting characters that are required by real form values (for
// example email addresses, phone numbers and dates), while rejecting markup,
// script and shell metacharacters from free-text fields.
const TEXT_VALUE_PATTERN = /[^\p{L}\p{M}\p{N}\s@._,'’()+/:-]/gu;
const EMAIL_VALUE_PATTERN = /[^\p{L}\p{M}\p{N}@._+-]/gu;
const TELEPHONE_VALUE_PATTERN = /[^0-9+()\s-]/g;
const NUMBER_VALUE_PATTERN = /[^0-9.-]/g;

const ignoredInputTypes = new Set([
  "button",
  "checkbox",
  "color",
  "date",
  "datetime-local",
  "file",
  "hidden",
  "image",
  "month",
  "radio",
  "range",
  "reset",
  "submit",
  "time",
  "week",
]);

export const sanitizeFormValue = (value, type = "text") => {
  if (typeof value !== "string") return value;

  switch (type) {
    case "email":
      return value.replace(EMAIL_VALUE_PATTERN, "");
    case "tel":
      return value.replace(TELEPHONE_VALUE_PATTERN, "");
    case "number":
      return value.replace(NUMBER_VALUE_PATTERN, "");
    default:
      return value.replace(TEXT_VALUE_PATTERN, "");
  }
};

const getFieldType = (field) => {
  if (field.tagName === "TEXTAREA") return "text";
  return (field.getAttribute("type") || "text").toLowerCase();
};

const isFilterableField = (field) => {
  if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) {
    return false;
  }

  if (field.hasAttribute("data-allow-special-characters")) return false;
  return !ignoredInputTypes.has(getFieldType(field));
};

const InputSecurity = () => {
  useEffect(() => {
    const sanitizeField = (event) => {
      const field = event.target;
      if (!isFilterableField(field)) return;

      const sanitizedValue = sanitizeFormValue(field.value, getFieldType(field));
      if (sanitizedValue !== field.value) {
        field.value = sanitizedValue;
      }
    };

    // Capture runs before React's delegated onChange handler, so controlled
    // component state receives only the sanitized value. It also covers paste,
    // drag/drop, autofill and mobile keyboards.
    document.addEventListener("input", sanitizeField, true);
    return () => document.removeEventListener("input", sanitizeField, true);
  }, []);

  return null;
};

export default InputSecurity;
